import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  getClients, createClient, updateClient, deleteClient, setClientPortal, PRICE_LEVELS,
} from "@/lib/api";
import { Plus, Trash2, Pencil, Building2, KeyRound, Check, X, Star, MapPin } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const empty = {
  id: null, name: "", price_level: 1, contact_person: "", email: "", phone: "",
  address: "", addresses: [], tax_id: "", reg_no: "", notes: "",
  requires_custom_order_no: false, requires_invoice_no: false,
  create_login: false, login_email: "", login_password: "",
};

const levelColor = {
  1: "bg-secondary text-muted-foreground border border-border",
  2: "badge-ok", 3: "badge-ok", 4: "badge-warn", 5: "bg-[hsl(var(--brand))] text-white",
};

export default function Clients() {
  const { t } = useT();
  const [clients, setClients] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [portal, setPortal] = useState(null);

  const load = async () => setClients(await getClients());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => { await load(); })(); }, []);

  const save = async (f) => {
    try {
      if (!f.name) { toast.error(t("clients.nameRequired")); return; }
      if (f.id) {
        await updateClient(f.id, {
          name: f.name, price_level: Number(f.price_level), contact_person: f.contact_person,
          email: f.email, phone: f.phone, address: f.address, addresses: f.addresses || [],
          tax_id: f.tax_id, reg_no: f.reg_no, notes: f.notes,
          requires_custom_order_no: !!f.requires_custom_order_no, requires_invoice_no: !!f.requires_invoice_no,
        });
      } else {
        if (f.create_login && (!(f.login_email || f.email) || !f.login_password)) {
          toast.error(t("clients.loginNeeds")); return;
        }
        await createClient({
          name: f.name, price_level: Number(f.price_level), contact_person: f.contact_person,
          email: f.email, phone: f.phone, address: f.address, addresses: f.addresses || [],
          tax_id: f.tax_id, reg_no: f.reg_no, notes: f.notes,
          requires_custom_order_no: !!f.requires_custom_order_no, requires_invoice_no: !!f.requires_invoice_no,
          create_login: f.create_login, login_email: f.login_email || f.email, login_password: f.login_password,
        });
      }
      toast.success(t("clients.saved"));
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const remove = async (id) => {
    try { await deleteClient(id); toast.success(t("clients.removed")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const savePortal = async (p) => {
    try {
      if (!p.login_email || !p.login_password) { toast.error(t("clients.emailPwRequired")); return; }
      await setClientPortal(p.id, { login_email: p.login_email, login_password: p.login_password });
      toast.success(t("clients.portalUpdated"));
      setPortal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <Layout actions={
      <Button size="sm" onClick={() => setDialog({ ...empty })} data-testid="add-client-button">
        <Plus className="mr-1.5 h-4 w-4" /> {t("clients.add")}
      </Button>
    }>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("clients.title")} ({clients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <Building2 className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("clients.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("clients.level", { n: "" }).trim()}</TableHead>
                    <TableHead>{t("clients.contact")}</TableHead>
                    <TableHead>{t("common.phone")}</TableHead>
                    <TableHead>{t("clients.portal")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((c) => (
                    <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                      <TableCell className="font-medium">
                        {c.name}
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </TableCell>
                      <TableCell><Badge className={levelColor[c.price_level]}>{t("clients.level", { n: c.price_level })}</Badge></TableCell>
                      <TableCell className="text-muted-foreground">{c.contact_person || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">{c.phone || "-"}</TableCell>
                      <TableCell>
                        {c.has_login
                          ? <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]"><Check className="h-3.5 w-3.5" /> {c.login_email}</span>
                          : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><X className="h-3.5 w-3.5" /> {t("clients.none")}</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" title={t("clients.portalAccess")} onClick={() => setPortal({ id: c.id, login_email: c.login_email || c.email || "", login_password: "" })} data-testid={`client-portal-${c.id}`}>
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDialog({ ...empty, ...c })} data-testid={`client-edit-${c.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`client-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ClientDialog key={dialog ? `client-${dialog.id ?? "new"}` : "client-closed"} dialog={dialog} onClose={() => setDialog(null)} onSave={save} />
      <PortalDialog key={portal ? `portal-${portal.id ?? "new"}` : "portal-closed"} portal={portal} onClose={() => setPortal(null)} onSave={savePortal} />
    </Layout>
  );
}

function ClientDialog({ dialog, onClose, onSave }) {
  const { t } = useT();
  const [f, setF] = useState(() => {
    if (!dialog) return dialog;
    const addresses = dialog.addresses && dialog.addresses.length
      ? dialog.addresses.map((a) => ({ ...a }))
      : (dialog.address ? [{ id: undefined, label: "", address: dialog.address, is_default: true }] : []);
    return { ...dialog, addresses };
  });
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const addrs = f.addresses || [];
  const newAddrId = () => (crypto?.randomUUID?.() || `addr-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const addAddr = () => setF((x) => ({
    ...x,
    addresses: [...(x.addresses || []), { id: newAddrId(), label: "", address: "", is_default: (x.addresses || []).length === 0 }],
  }));
  const setAddr = (i, key, val) => setF((x) => ({
    ...x, addresses: (x.addresses || []).map((a, idx) => (idx === i ? { ...a, [key]: val } : a)),
  }));
  const setDefaultAddr = (i) => setF((x) => ({
    ...x, addresses: (x.addresses || []).map((a, idx) => ({ ...a, is_default: idx === i })),
  }));
  const rmAddr = (i) => setF((x) => {
    const next = (x.addresses || []).filter((_, idx) => idx !== i);
    if (next.length && !next.some((a) => a.is_default)) next[0].is_default = true;
    return { ...x, addresses: next };
  });

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{f.id ? t("clients.editClient") : t("clients.addClient")}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>{t("clients.companyName")}</Label>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="client-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("clients.priceLevel")}</Label>
              <Select value={String(f.price_level)} onValueChange={(v) => set("price_level", v)}>
                <SelectTrigger data-testid="client-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRICE_LEVELS.map((l) => <SelectItem key={l} value={String(l)}>{t("clients.level", { n: l })}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("clients.contactPerson")}</Label>
              <Input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="client-contact-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.email")}</Label>
              <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="client-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.phone")}</Label>
              <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="client-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("clients.taxId")}</Label>
              <Input value={f.tax_id} onChange={(e) => set("tax_id", e.target.value)} data-testid="client-taxid-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("clients.regNo")}</Label>
              <Input value={f.reg_no} onChange={(e) => set("reg_no", e.target.value)} data-testid="client-regno-input" />
            </div>

            <div className="space-y-2 col-span-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {t("clients.addresses")}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAddr} data-testid="client-add-address-button">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t("clients.addAddress")}
                </Button>
              </div>
              {addrs.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("clients.noAddresses")}</p>
              ) : (
                <div className="space-y-2">
                  {addrs.map((a, i) => (
                    <div key={a.id || i} className="rounded-lg border border-border p-2.5 space-y-2" data-testid={`client-address-row-${i}`}>
                      <div className="grid grid-cols-3 gap-2">
                        <Input className="col-span-1" placeholder={t("clients.addrLabelPh")} value={a.label || ""} onChange={(e) => setAddr(i, "label", e.target.value)} data-testid={`client-address-label-${i}`} />
                        <Input className="col-span-2" placeholder={t("clients.addrLinePh")} value={a.address || ""} onChange={(e) => setAddr(i, "address", e.target.value)} data-testid={`client-address-line-${i}`} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Button
                          type="button"
                          variant={a.is_default ? "default" : "outline"}
                          size="sm"
                          onClick={() => setDefaultAddr(i)}
                          data-testid={`client-address-default-${i}`}
                        >
                          <Star className={`mr-1 h-3.5 w-3.5 ${a.is_default ? "fill-current" : ""}`} />
                          {a.is_default ? t("clients.defaultAddr") : t("clients.setDefault")}
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => rmAddr(i)} data-testid={`client-address-remove-${i}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5 col-span-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="client-notes-input" />
            </div>
          </div>

          <div className="rounded-lg border border-border p-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground">{t("clients.docRefs")}</p>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">{t("clients.reqCustomOrderNo")}</Label>
                <p className="text-xs text-muted-foreground">{t("clients.reqCustomOrderNoHint")}</p>
              </div>
              <Switch checked={!!f.requires_custom_order_no} onCheckedChange={(v) => set("requires_custom_order_no", v)} data-testid="client-req-order-no-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">{t("clients.reqInvoiceNo")}</Label>
                <p className="text-xs text-muted-foreground">{t("clients.reqInvoiceNoHint")}</p>
              </div>
              <Switch checked={!!f.requires_invoice_no} onCheckedChange={(v) => set("requires_invoice_no", v)} data-testid="client-req-invoice-no-switch" />
            </div>
          </div>
          {!f.id && (
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">{t("clients.createLogin")}</Label>
                  <p className="text-xs text-muted-foreground">{t("clients.createLoginHint")}</p>
                </div>
                <Switch checked={f.create_login} onCheckedChange={(v) => set("create_login", v)} data-testid="client-create-login-switch" />
              </div>
              {f.create_login && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("clients.loginEmail")}</Label>
                    <Input type="email" value={f.login_email} onChange={(e) => set("login_email", e.target.value)} placeholder={f.email} data-testid="client-login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("clients.password")}</Label>
                    <Input type="password" value={f.login_password} onChange={(e) => set("login_password", e.target.value)} data-testid="client-login-password-input" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="client-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PortalDialog({ portal, onClose, onSave }) {
  const { t } = useT();
  const [p, setP] = useState(portal);
  if (!p) return null;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={!!portal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("clients.portalAccess")}</DialogTitle>
          <DialogDescription>{t("clients.portalDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("clients.loginEmail")}</Label>
            <Input type="email" value={p.login_email} onChange={(e) => set("login_email", e.target.value)} data-testid="portal-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("clients.password")}</Label>
            <Input type="password" value={p.login_password} onChange={(e) => set("login_password", e.target.value)} data-testid="portal-password-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(p)} data-testid="portal-save-button">{t("clients.saveAccess")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
